import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import { DocInfoModalComponent } from 'src/app/modals/doc-info-modal/doc-info-modal.component';
import { NewTalonComponentMobile } from 'src/app/modals/new-talon-mobile/talon.component';
import { ConfigService } from 'src/app/services/application/config.service.';
import { CalendarService } from 'src/app/services/calendar.service';
import { RecordService } from 'src/app/services/record.service';
import { Utils } from 'src/app/services/utils';
import { AlertService } from 'src/lib/ariadna/alert';
import {
    IDepartment,
    IDep,
    IDoctor,
    IFilial,
    IRecTalon,
    IRnumbDate,
    IServ,
    ITalonModal,
    ITalonsDaySetParams,
} from '../../../interfaces/record.interfaces';
import {
    formatApiDate,
    formatUiDate,
    getDateKey,
    toDateTime,
    uniqueDateTimesByDay,
} from '../../../shared/date/date.utils';
import { strToDate } from '../../application/global.function';
import type { IFilialDep } from './interfaces';

@Component({
    selector: 'app-mobile-appointment',
    templateUrl: './mobile-appointment.component.html',
    styleUrls: ['./mobile-appointment.component.scss'],
    standalone: false,
})
export class MobileAppointmentComponent implements OnInit {
    @Input() public params!: IRnumbDate;
    @Output() public talonPickerClosed = new EventEmitter();
    @ViewChild('calendar') public calendar!: MatCalendar<DateTime>;

    public showCalendarIcon: boolean = true;
    public isCalendarActive: boolean = false;
    public showDropdown: boolean = false;
    public isSlideRight: boolean = false;
    public isSlideLeft: boolean = false;
    public selectedDay: DateTime | null = DateTime.local();
    public selectedSrv: IServ | null = null;
    public activeDays: DateTime[] = []; // NOT sorted by date
    public talonListFiltered: IRecTalon[] = [];
    public loading: boolean = false;
    public filialFromParams: IFilial | null = null;

    public pickedTalon: IRecTalon | null = null;
    public selectedDeps: Array<number> = []; // Список выбранных депов
    public depPicked: boolean = false;
    public selectedDep: IDep | null = null;
    public headerTxt: string = '';
    public depList: IDepartment[] = [];

    public constructor(
        private configS: ConfigService,
        private rs: RecordService,
        private alertService: AlertService,
        private cs: CalendarService,
        private modal: MatDialog,
        private modalService: NgbModal,
    ) {}

    public ngOnInit(): void {
        this.loading = true;

        this.activeDays = this.filterActiveDays();
        if (this.activeDays.length > 0) {
            this.selectedDay = this.findFirstActiveDay();
            this.updateHeaderText();
        }
    }

    public minDate: DateTime = DateTime.local().startOf('year');
    public maxDate: DateTime = DateTime.local().endOf('year');
    public currentDay: Date | null | undefined = null;

    public async ngOnChanges(changes: any) {
        this.filialFromParams = this.params?.Idep
            ? Array.isArray(this.params.Idep)
                ? this.params.Idep[0] || null // ← Берём первый элемент или null
                : this.params.Idep // ← Если это не массив — используем напрямую
            : null;
        if (changes.recDoctors) {
            if (changes.recDoctors.currentValue) {
                this.loading = false;
            }
        }

        if (changes.params) {
            this.loading = true;
            try {
                this.maxDate = toDateTime(
                    changes.params.currentValue.periodEnd,
                );
                await Promise.all([
                    await this.getTalonList(changes.params.currentValue),
                    await this.getDepList(changes.params.currentValue),
                ]).catch((e) => {
                    this.alertService.error(
                        'Не удалось получить список талонов',
                    );
                    console.error(e);
                });

                this.selectedDep =
                    this.depList.find(
                        (dep) => dep.text === this.filialFromParams?.depName,
                    ) || this.depList.length === 1
                        ? this.depList[0]
                        : null;
                this.selectedDeps =
                    this.depList.find(
                        (dep) => dep.text === this.filialFromParams?.depName,
                    )?.mergedKeyIds || [];

                this.activeDays = this.filterActiveDays();
                this.selectedDay = this.findFirstActiveDay();
                this.talonListFiltered = this.filterTalons();

                this.updateCalendar();
                this.loading = false;
                this.pickedTalon = null;
                this.currentDay = this.selectedDay?.toJSDate();
                const data: ITalonsDaySetParams = {
                    dt: this.currentDay ? this.currentDay : new Date(),
                    dep: this.selectedDep ? this.selectedDep : undefined,
                };
                this.initDay(data);
            } catch (e) {
                this.alertService.error('Ошибка получения номерков');
                setTimeout(() => {
                    this.closeComponent();
                }, 1000);
                this.loading = false;
                console.error(e);
            }
        }
    }

    public initDay(data: ITalonsDaySetParams) {
        // Фильтр по дню
        // this.selectedDate = data.dt;
        const depId = data.dep && data.dep.keyId > 0 ? data.dep.keyId : 0;

        this.loadDate(depId);
    }
    public talonListDay: IRecTalon[] = []; // Массов дней - активный
    public calendarEnabled: boolean = true;

    public loadDate(depId: number) {
        const dateFilter = getDateKey(this.selectedDay);
        this.headerTxt = formatUiDate(this.selectedDay);
        this.talonListDay = this.talonList.filter(
            (item) =>
                item.dtFilter === dateFilter &&
                (item.depId === depId || depId === 0),
        );
        const dates = this.talonListAll
            .map((item) => item.dtFilter)
            .filter((date, index, self) => {
                return self.indexOf(date) === index;
            });
        const months = this.talonListAll
            .map((item) => String(item.dtFilter).substr(0, 6))
            .filter((month, index, self) => {
                return self.indexOf(month) === index;
            });
        this.calendarEnabled = dates.length > 1;

        this.cs.setParams({
            year_begin: DateTime.local().year,
            mounth_begin: DateTime.local().month,
            count: months.length,
            cdActive: dates.map((date) => ({
                date: date !== undefined ? date : 0,
            })),
            orderByStart: true,
            MinDate: {
                m: DateTime.local().month,
                y: DateTime.local().year,
            },
            selectDate: '',
        });
        this.cs.setSelectedDate(formatApiDate(this.selectedDay));
    }

    public updateCalendar() {
        // if (this.calendar) this.calendar.updateTodaysDate();
    }
    public findFirstActiveDay() {
        let firstActiveDay = this.activeDays[0];
        this.activeDays.forEach((day) => {
            if (firstActiveDay.toMillis() > day.toMillis()) {
                firstActiveDay = day;
            }
        });
        return firstActiveDay;
    }
    public talonListAll: IRecTalon[] = [];
    public talonList: IRecTalon[] = []; // Массов дней - активный
    private async getTalonList(params: IRnumbDate) {
        try {
            this.talonListAll = await lastValueFrom(
                this.rs.getRnumbListv2(params),
            );

            this.talonListAll.forEach((talon, i) => {
                this.talonListAll[i].dtBegin = strToDate(talon.datBeginStr);
            });
        } catch (error) {
            throw new Error('Failed to fetch talon list');
        }
    }

    private filterActiveDays() {
        return uniqueDateTimesByDay(
            this.talonListAll.map((talon) =>
                toDateTime(strToDate(talon.datBeginStr)),
            ),
        );
    }

    private async getDepList(params: IRnumbDate) {
        try {
            // Получаем полный список подразделений
            const fullDepList: Array<IDep> = await lastValueFrom(
                this.rs.getDepListv2(params),
            );

            // Получаем уникальные depid из talonListAll
            const depsWithTalons = new Set<number>(
                this.talonListAll.map((talon) => Number(talon.depId)),
            );

            // Фильтруем подразделения, у которых есть талоны
            const filteredByTalons = fullDepList.filter((dep: IDep) =>
                depsWithTalons.has(Number(dep.keyId)),
            ); // Теперь фильтруем изначально полученный список депов беря только те, у которых есть талоны

            this.depList = this.mergeDuplicByText(filteredByTalons);

            // Добавляем опцию "Все" в начало
            // this.depList.unshift({ keyid: -1, text: 'Все' });
        } catch (error) {
            console.error('Failed to fetch department list', error);
            this.alertService.error(
                'Не удалось загрузить список подразделений',
            );
            // this.depList = [{ keyid: -1, text: 'Все' }]; // fallback
        }
    }

    private mergeDuplicByText(deps: IDep[]): IDep[] {
        const map = new Map<string, IDep>();
        for (const dep of deps) {
            const existing = map.get(dep.text);
            if (existing) {
                existing.mergedKeyIds!.push(dep.keyId);
            } else {
                map.set(dep.text, { ...dep, mergedKeyIds: [dep.keyId] });
            }
        }
        return Array.from(map.values());
    }

    private removeDuplicates<T>(arr: T[], key: keyof T): T[] {
        const seen = new Set<string | number>();
        return arr.filter((item) => {
            const k = item[key] as string | number;
            if (seen.has(k)) {
                return false;
            }
            seen.add(k);
            return true;
        });
    }

    public closeComponent(): void {
        this.talonPickerClosed.emit();
    }

    public calendarHandler(): void {
        this.isCalendarActive = !this.isCalendarActive;
    }

    public getDocImg(docId?: number): string {
        if (!docId) return '';
        return `${this.configS.getValue('hostBackend')}/img/doc/${docId}.png`;
    }

    public getDocImgDefault(event: Event): void {
        const elem = event.target as HTMLImageElement;
        // elem.src = `${this.configS.getValue('hostBackend')}/img/doc/not.png`;
        elem.src = './assets/images/placeholders/doctor-photo.png';
    }

    public getFullName(doctor?: IDoctor | null): string {
        if (!doctor) return '';
        return `${doctor.lName || ''} ${doctor.fName || ''} ${
            doctor.sName || ''
        }`.trim();
    }

    public openDocInfoModal(doc: IDoctor | null, event: Event) {
        event.stopPropagation();

        const modal = this.modalService.open(DocInfoModalComponent);
        modal.componentInstance.doc = doc;
        modal.componentInstance.spec = this.params?.selectedSpec?.text;
    }

    public toggleDropdown(): void {
        this.showDropdown = !this.showDropdown;
    }

    public placeholderDep(): string {
        if (this.filialFromParams) {
            return this.filialFromParams.depName || 'Выберите адрес';
        } else {
            if (this.selectedDep) {
                return this.selectedDep.text;
            }
        }
        return 'Выберите адрес';
    }

    public onDateSelected(date: DateTime): void {
        this.selectedDay = date;
        // this.isCalendarActive = false;
        this.updateHeaderText();
        this.talonListFiltered = this.filterTalons();
    }

    public prevDate(): void {
        this.pickedTalon = null;
        if (!this.selectedDay) return;
        const currentDate = this.selectedDay.startOf('day');

        // собираем уникальные даты из talonListAll
        const uniqueDates = [
            ...new Set(
                this.talonListAll.map((talon) =>
                    toDateTime(talon.dtBegin).startOf('day').toMillis(),
                ),
            ),
        ].sort((a, b) => a - b);

        const currentIndex = uniqueDates.findIndex(
            (date) => date === currentDate.toMillis(),
        );

        if (currentIndex > 0) {
            const prevDate = toDateTime(uniqueDates[currentIndex - 1]);
            this.clickDate(prevDate);
            this.talonListFiltered = this.filterTalons();
        }
        //Анимация
        this.isSlideLeft = true;
        setTimeout(() => {
            this.isSlideLeft = false;
        }, 1000);
    }

    public nextDate(): void {
        this.pickedTalon = null;
        if (!this.selectedDay) return;
        const currentDate = this.selectedDay.startOf('day');

        // собираем уникальные даты из talonListAll
        const uniqueDates = [
            ...new Set(
                this.talonListAll.map((talon) =>
                    toDateTime(talon.dtBegin).startOf('day').toMillis(),
                ),
            ),
        ].sort((a, b) => a - b);

        const currentIndex = uniqueDates.findIndex(
            (date) => date === currentDate.toMillis(),
        );

        if (currentIndex < uniqueDates.length - 1) {
            const nextDate = toDateTime(uniqueDates[currentIndex + 1]);
            this.clickDate(nextDate);
            this.talonListFiltered = this.filterTalons();
        }
        //Анимация
        this.isSlideRight = true;
        setTimeout(() => {
            this.isSlideRight = false;
        }, 1000);
        this.updateHeaderText();
    }

    public clickDate(date: DateTime) {
        this.selectedDay = date;

        if (!date) {
            return;
        }
        if (this.selectedDep) {
            // Убедимся, что используем правильное поле keyid
            this.loadDate(this.selectedDep.keyId);
        }
    }

    public isFirstDate(): boolean {
        if (!this.selectedDay || !this.talonListAll.length) return true;

        const uniqueDates = [
            ...new Set(
                this.talonListAll.map((talon) =>
                    toDateTime(talon.dtBegin).startOf('day').toMillis(),
                ),
            ),
        ].sort((a, b) => a - b);

        return this.selectedDay.startOf('day').toMillis() === uniqueDates[0];
    }

    public isLastDate(): boolean {
        if (!this.selectedDay || !this.talonListAll.length) return true;

        const uniqueDates = [
            ...new Set(
                this.talonListAll.map((talon) =>
                    toDateTime(talon.dtBegin).startOf('day').toMillis(),
                ),
            ),
        ].sort((a, b) => a - b);

        return (
            this.selectedDay.startOf('day').toMillis() ===
            uniqueDates[uniqueDates.length - 1]
        );
    }

    public toggleClass(): void {
        this.calendarHandler();
    }

    public clickTalon(event: Event, talon: IRecTalon): void {
        // if (this.talonDisable(talon)) {
        this.pickedTalon = talon;
        // }
    }

    /* Блокировка выбора талона */
    public talonDisable(talon: IRecTalon): boolean {
        if (!this.params?.srv) return false;
        return this.params.srv.isTelemed === talon.isTelemed;
    }

    public async openTalonModal(event: MouseEvent, talon: IRecTalon | null) {
        event.stopPropagation();
        if (this.pickedTalon) {
            talon = this.pickedTalon;
        }

        if (talon?.rnumbId !== this.pickedTalon?.rnumbId) {
            this.pickedTalon = talon;
        }

        if (this.params?.srv?.isTelemed !== talon?.isTelemed) {
            this.loading = false;
            return;
        }

        // if (!talon) return;
        let data: ITalonModal = {
            rnumbId: null,
            info: {
                docdepID: this.pickedTalon?.docDepId,
                beginDate: this.pickedTalon?.dtBegin,
            },
            srv: this.params.srv,
            paramCansel: false,
            infoDoc: this.params.infoDoc,
        };

        try {
            if (talon?.isInterval && talon.isInterval > 0 && talon.intervalId) {
                // console.log(talon);
                const { guid } = await lastValueFrom(
                    this.rs.getRnumbCreateInterval(
                        talon?.intervalId,
                        talon?.datBeginStr,
                        talon?.datEndStr,
                    ),
                );

                if (!guid)
                    throw new Error('Не удалось создать интервальный номерок');
                data.rnumbId = guid;
                await lastValueFrom(
                    this.rs.setRnumbBlStatus(data.rnumbId as number),
                );
            } else if (talon?.rnumbId) {
                data.rnumbId = talon.rnumbId;

                await lastValueFrom(
                    this.rs.setRnumbBlStatus(data.rnumbId as number),
                );
            }

            this.alertService.success(
                `Талон успешно зарезервирован, № талона: ${data.rnumbId}`,
            );

            let dialogRefOptions = {
                width: 'auto',
                maxWidth: 'auto',
                minWidth: 'auto',
                closeOnNavigation: true,
                data,
            };

            // Check screen width and adjust dialog options if needed
            if (window.matchMedia('(max-width: 767px)').matches) {
                dialogRefOptions.width = '90vw';
                dialogRefOptions.maxWidth = '100vw';
                dialogRefOptions.minWidth = '90vw';
            }

            const modalRef = this.modal.open(
                NewTalonComponentMobile,
                dialogRefOptions,
            );

            modalRef.afterClosed().subscribe((result) => {
                // console.log(result);
                if (result && result.unlockTalon) {
                    lastValueFrom(this.rs.getRnumbUnlock(result.rnumbID))
                        .then((value) => {
                            this.alertService.success(
                                `Талон успешно разблокирован, № талона: ${result.rnumbID}`,
                            );
                        })
                        .catch((error: any) => {
                            this.alertService.error(
                                `Не удалось разблокировать талон с id ${result.rnumbID}: ${Utils.getErrorMessage(error)}`,
                            );
                        });
                }
            });
        } catch (error: any) {
            const errorMessage = Utils.getErrorMessage(error);
            console.error(`Не удалось создать интервал: ${errorMessage}`);
            this.alertService.error(
                `Не удалось записаться на номерок: ${errorMessage}`,
            );
        }
    }

    // buttonAcceptplaceholder(): string {
    //     return this.pickedTalon ? 'Выбрать' : 'Выберите время';
    // }

    public buttonAcceptplaceholder() {
        if (this.pickedTalon) {
            if (this.params?.srv?.isTelemed !== this.pickedTalon?.isTelemed) {
                return 'Это номерок для другой услуги';
            }
            return 'Выбрать';
        }
        return 'Выберите время';
    }

    private updateHeaderText(): void {
        if (this.selectedDay) {
            this.headerTxt = formatUiDate(this.selectedDay);
        }
    }

    public selectDep(dep: IDepartment) {
        this.pickedTalon = null;
        this.showCalendarIcon = true;
        if (dep.mergedKeyIds && dep.mergedKeyIds.length > 1) {
            this.selectedDeps = dep.mergedKeyIds;
        }
        this.headerTxt = formatUiDate(this.selectedDay);

        // Преобразуем Department в IDep правильно
        this.selectedDep = dep
            ? {
                  keyId: dep.keyId,
                  text: dep.text,
              }
            : null;

        this.showDropdown = false;
        this.talonListFiltered = this.filterTalons();
        // }
        this.activeDays = this.filterActiveDays();
    }

    public filterTalons() {
        return this.talonListAll.filter((talon) => {
            return (
                Boolean(
                    this.selectedDay &&
                    talon.dtBegin &&
                    toDateTime(talon.dtBegin).hasSame(this.selectedDay, 'day'),
                ) &&
                (this.selectedDep
                    ? this.selectedDeps.length > 1 &&
                      this.selectedDeps.includes(this.selectedDep.keyId)
                        ? this.selectedDeps.includes(talon.depId)
                        : talon.depId === this.selectedDep.keyId
                    : true)
            );
        });
    }

    public get hasDoctorInfo(): boolean {
        return (
            Array.isArray(this.params?.infoDoc) &&
            this.params.infoDoc.length > 0
        );
    }
}
